<template>
    <div>
        <card-header title="Customize Event" icon="fa-cog"/>

        <entity-tabs v-model="tab" main-label="Event" main-icon="fa-cog" show-contacts :contact-count="contactCount" :task-count="taskCount" :task-completed-count="taskCompleted" :note-count="noteCount"/>

        <card-toolbar v-if="!orphaned && !notFound" v-show="tab === 'main'">
            <button @click="$openCard('new-event-document', {tripId: 'null', registrationId: props.registrationId, dayId: props.dayId, eventId: props.eventId, type: 'custom-event'}, card)"><i class="fas fa-plus mr-2"/>New Document</button>
        </card-toolbar>

        <card-body>
            <entity-contacts-pane v-show="tab === 'contacts'" :card="card" :child="child" entity-type="reg-events" :entity-id="props.eventId" @count="contactCount = $event"/>
            <entity-tasks-pane v-show="tab === 'tasks'" :card="card" :child="child" entity-type="reg-events" :entity-id="props.eventId" @count="taskCount = $event" @completed="taskCompleted = $event"/>
            <entity-notes-pane v-show="tab === 'notes'" :card="card" :child="child" entity-type="reg-events" :entity-id="props.eventId" @count="noteCount = $event"/>
            <div v-show="tab === 'main'">
            <card-list>
                <registration-list-item :card="card" :registration="registration"/>
                <container-list-item padded v-if="notFound">
                    <i class="fas fa-exclamation-triangle mr-2"></i>This customization could not be loaded. It may have already been removed.
                </container-list-item>
                <container-list-item padded v-if="orphaned">
                    <i class="fas fa-exclamation-triangle mr-2"></i><strong>This event no longer exists.</strong>
                    The trip event this customization belongs to was deleted, but the registration still references it.
                    Use the button below to remove the customization.
                </container-list-item>
                <event-list-item v-if="!orphaned && !notFound" :card="card" :event="dayEvent"/>
            </card-list>
            <override-form
                v-if="!orphaned && !notFound"
                :event="event"
                :trip-event="dayEvent"
                :trip-id="registration.trip_id"
                :registration-id="props.registrationId"
                :card="card"
                :child="child"
            />
            </div>
        </card-body>

        <card-footer class="pt-3 pb-3" v-show="tab === 'main'">
            <div class="columns">
                <div class="column" v-if="!notFound">
                    <b-button type="is-warning is-outlined" size="is-small" @click="deleteEvent"><i class="far fa-trash-alt"></i><span class="ml-2" v-if="confirmDelete">Remove Customization?</span><span class="ml-2" v-else-if="orphaned">Remove Customization</span></b-button>
                </div>
                <div class="column has-text-right" v-if="!orphaned && !notFound">
                    <b-button type="is-primary" size="is-small" @click="submit"><i class="fas fa-check mr-2"></i>Save Customization</b-button>
                </div>
            </div>
        </card-footer>
    </div>

</template>

<script>
    import async from "async";
    import {client as http} from "../../http_client";
    import CardHeader from "../../TIER/components/CardHeader";
    import CardBody from "../../TIER/components/CardBody";
    import CardFooter from "../../TIER/components/CardFooter";
    import EventForm from "../trips/EventForm";
    import OverrideForm from "./OverrideForm";
    import RegistrationListItem from "../../components/RegistrationListItem";
    import EventListItem from "../../components/EventListItem";
    import CardList from "../../TIER/components/CardList";
    import CardToolbar from "../../TIER/components/CardToolbar.vue";
    import ContainerListItem from "../../TIER/components/ContainerListItem";
    import {mapActions} from "vuex";
    import EntityTabs from "@/components/EntityTabs";
    import EntityTasksPane from "@/components/EntityTasksPane";
    import EntityContactsPane from "@/components/EntityContactsPane";
    import EntityNotesPane from "@/components/EntityNotesPane";

    export default {
        components: {EntityContactsPane, EntityNotesPane, EntityTasksPane, EntityTabs, CardToolbar, ContainerListItem, CardList, EventListItem, RegistrationListItem, OverrideForm, EventForm, CardFooter, CardBody, CardHeader},
        props: ['card', 'props', 'child'],
        data: function () {
            return {
                event: {},
                registration: {
                    trip: {},
                    user: {}
                },
                dayEvent: {},
                confirmDelete: false,
                dirty: null,
                orphaned: false,
                notFound: false,
                tab: 'main',
                contactCount: 0,
                taskCount: 0,
                taskCompleted: 0,
                noteCount: 0
            };
        },
        methods: {
            ...mapActions(['updateTasks']),
            loadEvent: function () {
                this.$emit('loading', true);
                this.dirty = null;
                this.orphaned = false;
                this.notFound = false;

                async.series([
                    // Get event
                    (cb) => {
                        http.get('/api/registrations/' + this.props.registrationId + '/events/' + this.props.dayId + '/' + this.props.eventId, {force: true}).then(response => {
                            this.event = response.data;
                            cb();
                        }).catch(cb);
                    },
                    // Get registration
                    (cb) => {
                        http.get('/api/registrations/' + this.props.registrationId, {force: true}).then(response => {
                            this.registration = response.data;
                            cb();
                        }).catch(cb);
                    },
                    (cb) => {
                        http.get('/api/trips/' + this.registration.trip_id + '/days/' + this.props.dayId + '/events/' + this.event.day_event_id, {force: true}).then(response => {
                            this.dayEvent = response.data;
                            cb();
                        }).catch(err => {
                            if (err.response && err.response.status === 404) {
                                // The trip event was deleted out from under this customization
                                this.orphaned = true;
                                cb();
                            } else {
                                cb(err);
                            }
                        });
                    }
                ], err => {
                    if (err) this.notFound = true;
                    this.$emit('loading', false);
                });
            },
            submit() {
                this.$emit('loading', true);

                http.put('/api/registrations/' + this.props.registrationId + '/events/' + this.props.eventId, this.event, {force: true}).then(response => {
                    this.updateTasks();
                    this.$reloadCard('registrations');
                    this.$reloadCard('registration', {registrationId: this.props.registrationId});
                    this.$reloadCard('itinerary', {registrationId: this.props.registrationId});
                    this.$reloadCard('events', {dayId: this.props.dayId});
                    this.$reloadCard('tasks');
                    this.$reloadCard('task-details', {type: 'reg-events'});
                    this.$saveAlert(false);
                    this.$closeCard(this.card);
                }).catch(err => {
                    this.$emit('loading', false);
                });
            },
            deleteEvent() {
                if (this.confirmDelete) {
                    this.$emit('loading', true);

                    http.delete('/api/registrations/' + this.props.registrationId + '/events/' + this.props.eventId).then(response => {
                        this.$reloadCard('tasks');
                        this.$reloadCard('events', {dayId: this.props.dayId});
                        this.$saveAlert(false);
                        this.$closeCard(this.card);
                    }).catch(err => {
                        this.$emit('loading', false);
                    });
                } else {
                    this.confirmDelete = true;
                    setTimeout(() => {
                        this.confirmDelete = false;
                    }, 3000);
                }
            }
        },
        watch: {
            'props.eventId': function () {
                this.loadEvent();
            },
            'event': {
                deep: true,
                handler: function () {
                    this.dirty = (this.dirty !== null);
                    if (!this.$TIER.saveAlert.active && this.dirty)
                        this.$saveAlert(true, 'Are you done customizing this event? Unsaved changes will be lost.');
                }
            }
        },
        mounted() {
            this.loadEvent();
        }
    };
</script>

<style scoped>

</style>
